import { ENVIRONMENT } from '@/environments/environment.ts';
import { loadTranslations } from '@/i18n';
import { setValidationError } from '@/lib/errorHandler.ts';
import customFetch, { login } from './customFetch';
import { UpdateUserPreferenceRequest } from '@/model/IUserPreference';

// BroadcastChannel para sincronizar tema entre abas/janelas
const themeChannel = new BroadcastChannel('theme_sync');

// Variável global para armazenar o ID do usuário atual
let currentUserId: number | null = null;

const getCurrentTheme = (): 'light' | 'dark' => {
    // 1. Primeiro, tenta pegar do localStorage
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null;
    if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) return savedTheme;

    // 2. Se não tiver no localStorage, pega a preferência do navegador
    const userPrefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    const themeFromSystem = userPrefersDark ? 'dark' : 'light';
    return themeFromSystem;
};

const setTheme = async (theme: 'light' | 'dark', userId?: number) => {
    // Aplicar o tema na UI
    applyThemeToUI(theme);

    // Salvar no localStorage
    localStorage.setItem('theme', theme);

    // Notificar outras abas/janelas sobre a mudança de tema
    themeChannel.postMessage({ type: 'theme-change', theme });

    // Persistir no backend se userId estiver disponível
    if (userId) {
        try {
            const requestBody: UpdateUserPreferenceRequest = {
                UserId: userId,
                IsDarkMode: theme === 'dark'
            };

            await customFetch(ENVIRONMENT.authApiPath.user.userPreference, {
                method: 'PUT',
                body: JSON.stringify(requestBody),
            });
        } catch (error) {
            console.error('Erro ao persistir tema no backend:', error);
            // Não reverter o tema; localStorage ainda foi atualizado
        }
    }
};

const getSunIcon = () => {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="5"></circle><line x1="12" y1="1" x2="12" y2="3"></line><line x1="12" y1="21" x2="12" y2="23"></line><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line><line x1="1" y1="12" x2="3" y2="12"></line><line x1="21" y1="12" x2="23" y2="12"></line><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line></svg>`;
};

const getMoonIcon = () => {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path><circle cx="18" cy="6" r="0.5" fill="currentColor"></circle><circle cx="20" cy="9" r="0.5" fill="currentColor"></circle><circle cx="19" cy="4" r="0.5" fill="currentColor"></circle></svg>`;
};

// Função para aplicar o tema na UI
const applyThemeToUI = (theme: 'light' | 'dark') => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    document.documentElement.style.colorScheme = theme;

    // Atualizar o botão de tema se existir
    const button = document.getElementById('themeToggleButton') as HTMLButtonElement;
    if (button) {
        button.innerHTML = theme === 'dark' ? getSunIcon() : getMoonIcon();
    }
};

// Listener para sincronizar tema entre abas/janelas
themeChannel.onmessage = (event) => {
    if (event.data.type === 'theme-change') {
        const theme = event.data.theme as 'light' | 'dark';
        applyThemeToUI(theme);
    }
};

// Função para buscar e aplicar preferência de tema do usuário do backend
const fetchAndApplyUserTheme = async (userId: number) => {
    try {
        const response = await customFetch<any>(
            `${ENVIRONMENT.authApiPath.user.userPreference}?UserId=${userId}`
        );

        if (response && 'errorMessage' in response) {
            const fallbackTheme = getCurrentTheme();
            applyThemeToUI(fallbackTheme);
            localStorage.setItem('theme', fallbackTheme);
            return;
        }

        // A resposta vem com estrutura { data: string (JSON), success: boolean }
        // onde data é uma string JSON que precisa ser parseada
        if (response && response.data && typeof response.data === 'string') {
            try {
                // Deserializar o JSON que está dentro de data
                const preferences = JSON.parse(response.data);

                if (preferences.theme) {
                    const theme = preferences.theme as 'light' | 'dark';
                    // Aplicar tema sem persistir no backend (já está salvo)
                    applyThemeToUI(theme);
                    localStorage.setItem('theme', theme);
                    themeChannel.postMessage({ type: 'theme-change', theme });
                    return;
                }
            } catch (parseError) {
                console.error('❌ Erro ao fazer parse do data:', parseError);
            }
        }
        const fallbackTheme = getCurrentTheme();
        applyThemeToUI(fallbackTheme);
        localStorage.setItem('theme', fallbackTheme);
    } catch (error) {
        console.error('❌ Erro ao buscar tema do usuário:', error);
        // Fallback para localStorage/sistema
        const fallbackTheme = getCurrentTheme();
        applyThemeToUI(fallbackTheme);
        localStorage.setItem('theme', fallbackTheme);
    }
};

// Aplicar o tema inicial ao carregar a página (antes da autenticação)
const initializeTheme = () => {
    try {
        const theme = getCurrentTheme();

        // Aplicar o tema na UI
        applyThemeToUI(theme);

        // Garantir que está salvo no localStorage
        if (!localStorage.getItem('theme')) {
            localStorage.setItem('theme', theme);
        }
    } catch (error) {
        console.error('Erro ao inicializar tema:', error);
        // Fallback para light
        applyThemeToUI('light');
    }
};

// Inicializar tema imediatamente (antes de qualquer outro código)
initializeTheme();

export const applicationLoadStatus = {
    Error: 1,
    Loading: 2,
    Finished: 3,
    UserNotFound: 4,
    AccessRestricted: 5,
};

export const loadOnePlaceScript = async (setOnePlaceFinished, setUser) => {
    try {
        const onePlace = ENVIRONMENT.oneplace;

        if (!onePlace) {
            setValidationError(`Environment variable 'oneplace' not found`);
            await setOnePlaceFinished(applicationLoadStatus.Error);
            return;
        }

        const node = document.createElement('script');
        node.src = onePlace;
        node.type = 'text/javascript';
        node.charset = 'utf-8';
        node.id = 'opHelper';

        node.onload = async function () {
            const openHeaderResponse = await openOnePlaceHeader(
                setOnePlaceFinished,
                setUser,
            );
            await customFetch<any>(ENVIRONMENT.authApiPath.basePath + '/getVersion').then(response => addBuildVersionIntoFooter(response.data));
            if (openHeaderResponse !== applicationLoadStatus.Finished) return;
        };
        document.getElementsByTagName('head')[0].appendChild(node);
    } catch (e) {
        setValidationError(`${e?.message}`);
        await setOnePlaceFinished(applicationLoadStatus.Error);
    }
};

const getOnePlace = () => {
    const onePlace = window['$OP'];

    return onePlace;
};

export const getAccessToken = async () => {
    const onePlace = getOnePlace();
    let token;
    if (!!onePlace && typeof onePlace.loadChromeHeaderTool == 'function') {
        token = await onePlace.getUserJwtRsaToken();
    }
    return token;
};

const openOnePlaceHeader = async (setOnePlaceFinished, setUser) => {
    const onePlace = getOnePlace();
    let applicationStatus = applicationLoadStatus.Loading;
    if (!!onePlace && typeof onePlace.loadChromeHeaderTool == 'function') {
        const token: any = await getAccessToken();
        const location = window.location.href;
        if (
            token &&
            token.token &&
            !(
                location.includes('/error') ||
                location.includes('accessRestricted') ||
                location.includes('userNotFound')
            )
        ) {
            await login(token.token).then(async (result) => {
                if (result && 'errorMessage' in result) {
                    setOnePlaceFinished(applicationLoadStatus.AccessRestricted);
                    applicationStatus = applicationLoadStatus.AccessRestricted;
                    return applicationStatus;
                }

                await customFetch<any>(
                    ENVIRONMENT.authApiPath.basePath + '/user/GetCurrentUser?Save=false',
                    {},
                )
                    .then(async (user) => {
                        if (user?.errorMessage) {
                            if (user.statusCode === 401 || user.statusCode === 403) {
                                setOnePlaceFinished(applicationLoadStatus.AccessRestricted);
                                applicationStatus = applicationLoadStatus.AccessRestricted;
                                return applicationStatus;
                            }

                            if (
                                user.statusCode === 404 ||
                                user?.errorMessage.includes('users.notFound')
                            ) {
                                applicationStatus = applicationLoadStatus.UserNotFound;
                                return applicationStatus;
                            }

                            setOnePlaceFinished(applicationLoadStatus.Error);
                            applicationStatus = applicationLoadStatus.Error;
                            return applicationStatus;
                        }

                        setUser(user.data);

                        // Armazenar userId globalmente e buscar preferência de tema
                        if (user.data && user.data.id) {
                            currentUserId = user.data.id;
                            await fetchAndApplyUserTheme(user.data.id);
                        }
                    })
                    .catch(async () => {
                        setOnePlaceFinished(applicationLoadStatus.Error);
                        applicationStatus = applicationLoadStatus.Error;
                        return applicationStatus;
                    });
            });
        }

        const cultures = await customFetch<any>(
            ENVIRONMENT.authApiPath.basePath + '/culture/GetAllCultures',
        );

        if (cultures.data) {
            const selectedCulture = getSelectedCulture(cultures.data);

            const translations = await loadTranslations(selectedCulture.id);

            if (translations?.errorMessage) {
                await setOnePlaceFinished(applicationLoadStatus.Error);
                return;
            }

            await loadHeader(onePlace, cultures.data, selectedCulture);
        } else {
            await loadHeader(onePlace, [], null);
        }
        if (applicationLoadStatus.Loading === applicationStatus) {
            setOnePlaceFinished(applicationLoadStatus.Finished);
            applicationStatus = applicationLoadStatus.Finished;
        }

        return applicationStatus;
    }
};

const createThemeToggleButton = () => {
    const currentTheme = getCurrentTheme();
    const button = document.createElement('button');
    button.id = 'themeToggleButton';
    button.setAttribute('type', 'button');
    button.setAttribute('aria-label', 'Toggle theme');
    button.style.background = 'none';
    button.style.border = 'none';
    button.style.cursor = 'pointer';
    button.style.padding = '8px';
    button.style.display = 'inline-flex';
    button.style.alignItems = 'center';
    button.style.justifyContent = 'center';
    button.style.marginLeft = '8px';
    button.style.marginRight = '8px';
    button.style.fontSize = '18px';
    button.style.color = 'inherit';
    button.style.position = 'absolute';
    button.style.right = '220px';
    button.style.top = '50%';
    button.style.transform = 'translateY(-50%)';
    button.innerHTML = currentTheme === 'dark' ? getSunIcon() : getMoonIcon();

    button.addEventListener('click', async () => {
        const newTheme = getCurrentTheme() === 'dark' ? 'light' : 'dark';
        await setTheme(newTheme, currentUserId || undefined);
        button.innerHTML = newTheme === 'dark' ? getSunIcon() : getMoonIcon();
    });

    return button;
};

const loadHeader = async (onePlace, cultures, selectedCulture) => {
    let _gObj = null;

    const languageDropdownItemClick = function () {
        _gObj.setCaption(this.name);
        loadTranslations(this.value);
    };

    const options = {
        captionRightAlignment: true,
        captionActions: (function () {
            const ret = [];
            cultures.forEach((item) => {
                ret.push({
                    name: item.name,
                    value: item.id,
                    action: languageDropdownItemClick,
                });
            });
            return ret;
        })(),

        apps: { autoEnableInUseAppList: true },
        usePrefersColorScheme: ENVIRONMENT.enableDarkMode,
        siteShortName: 'Data Validation System',
        header: { container: 'headerDomId' },
        footer: {
            enabled: true,
            container: 'footerDomId',
            supportInfo: {
                text: 'Support',
                email: 'oneplacesupport@willistowerswatson.com',
                action: function () {
                    const link =
                        'mailto:' +
                        this.email +
                        '?subject=OnePlace Support ' +
                        document.title +
                        '&body=PageLink:' +
                        window.location.href;
                    window.location.href = link;
                },
            },
        },
        signOut: {
            enabled: true,
            onSignOut: async (url) => {
                try {
                    // Chamar o endpoint de logout da API
                    await fetch(`${ENVIRONMENT.authApiPath.user.userLogout}`, {
                        method: 'POST',
                        credentials: 'include', // Importante para enviar cookies de autenticação
                    });
                } catch (error) {
                    console.error('Erro ao realizar logout da aplicação:', error);
                } finally {
                    // Resetar userId ao fazer logout
                    currentUserId = null;
                    // Redirecionar para a URL do OnePlace, independentemente do resultado do logout da API
                    window.location.href = url;
                }
            },
        },
    };

    if (!document.getElementById('ChromeHeader')) {
        const obj = await onePlace.loadChromeHeaderTool(options);
        _gObj = obj;

        obj.setCaption(selectedCulture.name);

        // Usar MutationObserver para detectar quando o header é completamente carregado
        const observeHeader = () => {
            const headerDomId = document.getElementById('headerDomId');
            if (!headerDomId) {
                setTimeout(observeHeader, 100);
                return;
            }

            const observer = new MutationObserver(() => {
                if (!document.getElementById('themeToggleButton') && document.getElementById('ChromeHeader')) {
                    addThemeButtonToHeader();
                }
            });

            observer.observe(headerDomId, {
                childList: true,
                subtree: true,
                attributes: true,
            });

            // Tentar adicionar o botão imediatamente
            addThemeButtonToHeader();

            // Parar de observar após encontrar o botão
            setTimeout(() => {
                if (document.getElementById('themeToggleButton')) {
                    observer.disconnect();
                }
            }, 5000);
        };

        const addThemeButtonToHeader = () => {
            if (document.getElementById('themeToggleButton')) return;

            const header = document.getElementById('ChromeHeader');
            if (!header) return;

            // Garantir que o header tem position relative para o botão ser posicionado corretamente
            if (header.style.position !== 'absolute' && header.style.position !== 'fixed') {
                header.style.position = 'relative';
            }

            const themeButton = createThemeToggleButton();

            // Inserir o botão no final do header
            header.appendChild(themeButton);
        };

        observeHeader();
    }
};

const getSelectedCulture = (cultures: any[]) => {
    const storageCultureId = Number.parseInt(localStorage.getItem('cultureId'));
    let selectedCulture = null;
    selectedCulture = cultures.find((culture) => storageCultureId === culture.id);
    if (selectedCulture) return selectedCulture;

    return cultures.find((culture) => culture.isBase) || cultures[0];
};

export function AllowHomeRedirect() {
    const header = document.getElementById('ChromeHeader');
    if (header) {
        const mainLink = header.querySelector('a');
        if (mainLink) mainLink.href = '/';
    }

    const appIcon = document.getElementById('appicon');
    if (appIcon) {
        appIcon.addEventListener('click', function (e) {
            e.preventDefault();
            window.location.href = '/';
        });
    }
}

export function addBuildVersionIntoFooter(version: string) {
    const footer = document.getElementById('CommonFooter');

    if (footer && (ENVIRONMENT.buildVersion || version)) {
        const existing = document.getElementById('BuildVersionText');
        if (existing) existing.remove();

        const p = document.createElement('span');
        p.id = 'BuildVersionText';
        p.innerText = `${version ? `Version: ${version}` : ''}${ENVIRONMENT.buildVersion ? `  Build: ${ENVIRONMENT.buildVersion}` : ''}`;
        p.style.float = 'right';
        p.style.marginTop = '17px';
        p.style.marginRight = '15px';
        p.style.fontSize = '12px';
        p.style.color = '#5c6066';
        footer.insertAdjacentElement('beforeend', p);
    }
}
